import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

export default function ContactForm({ prefilledService = '' }) {
  const [searchParams] = useSearchParams()
  const vehicleParam = searchParams.get('koeretoej') || ''

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: prefilledService || (vehicleParam ? `Forespørgsel på ${vehicleParam}` : 'Temperaturstyret transport'),
    description: '',
    website_url: '', // Honeypot field for bots
  })

  useEffect(() => {
    if (vehicleParam) {
      setFormData(prev => ({
        ...prev,
        service: `Forespørgsel på ${vehicleParam}`,
        description: prev.description || `Hej, jeg vil gerne høre nærmere om tilgængelighed og pris for ${vehicleParam}.`
      }))
    }
  }, [vehicleParam])

  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [serverError, setServerError] = useState('')

  const validate = () => {
    const errs = {}
    if (!formData.name.trim()) errs.name = 'Venligst indtast dit navn'
    if (!formData.phone.trim()) {
      errs.phone = 'Venligst indtast dit telefonnummer'
    } else if (!/^[0-9+\s()-]{6,20}$/.test(formData.phone.trim())) {
      errs.phone = 'Ugyldigt telefonnummer'
    }
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast din e-mailadresse'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = 'Ugyldig e-mailadresse'
    }
    if (!formData.description.trim()) {
      errs.description = 'Venligst skriv en kort beskrivelse af din transportopgave'
    }
    return errs
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setServerError('')

    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setSubmitting(true)

    try {
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        service: formData.service,
        description: formData.description.trim(),
        website_url: formData.website_url, // honeypot
      }

      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        throw new Error(`Fejl ved indsendelse: status ${res.status}`)
      }

      setSubmitted(true)
    } catch (err) {
      console.error('[Webform error]', err)
      setServerError('Der opstod en fejl ved afsendelse af formularen. Kontakt os gerne direkte på transport4u.dk@gmail.com eller tlf. 50 35 20 35.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl p-8 sm:p-10 border border-slate-200/90 shadow-sm text-center">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600 mb-6 ring-8 ring-emerald-50">
          <i className="fa-solid fa-check text-2xl" aria-hidden="true" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 tracking-tight">
          Tak for din henvendelse!
        </h3>
        <p className="mt-2 text-slate-600 max-w-md mx-auto text-sm sm:text-base">
          Vi har modtaget din forespørgsel og vender hurtigt tilbage til dig.
        </p>

        <div className="mt-6 p-4 rounded-xl bg-sky-50 border border-sky-100 max-w-md mx-auto">
          <p className="text-xs font-semibold text-sky-900">
            Haster det? Ring til vores døgnvagt:
          </p>
          <a
            href="tel:+4550352035"
            className="mt-1 inline-flex items-center gap-2 text-lg font-extrabold text-sky-700 hover:text-sky-900 whitespace-nowrap"
          >
            <i className="fa-solid fa-phone text-sm" aria-hidden="true" />
            <span className="whitespace-nowrap">50 35 20 35</span>
          </a>
        </div>

        <button
          type="button"
          onClick={() => {
            setSubmitted(false)
            setFormData({
              name: '',
              phone: '',
              email: '',
              service: 'Temperaturstyret transport',
              description: '',
              website_url: '',
            })
          }}
          className="mt-8 text-xs font-semibold text-slate-500 hover:text-slate-800 underline underline-offset-4"
        >
          Send en ny besked
        </button>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="bg-white rounded-2xl p-6 sm:p-8 lg:p-10 border border-slate-200/90 shadow-sm"
    >
      {/* Honeypot field (mandatory antispam) */}
      <div className="sr-only absolute" aria-hidden="true">
        <label htmlFor="website_url">Website URL</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>

      <div className="space-y-5">
        {/* Navn */}
        <div>
          <label htmlFor="form-name" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
            Navn <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            id="form-name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Dit fulde navn eller firmanavn"
            className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${
              errors.name
                ? 'border-rose-400 ring-rose-200 focus:border-rose-500'
                : 'border-slate-200 hover:border-slate-300 focus:border-sky-500 focus:ring-sky-100'
            }`}
          />
          {errors.name && (
            <p className="mt-1 text-xs text-rose-600 flex items-center gap-1 font-medium">
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
              {errors.name}
            </p>
          )}
        </div>

        {/* 2-col on sm: Telefon & Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Telefonnummer */}
          <div>
            <label htmlFor="form-phone" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
              Telefonnummer <span className="text-rose-500">*</span>
            </label>
            <input
              type="tel"
              id="form-phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="F.eks. 50 35 20 35"
              className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${
                errors.phone
                  ? 'border-rose-400 ring-rose-200 focus:border-rose-500'
                  : 'border-slate-200 hover:border-slate-300 focus:border-sky-500 focus:ring-sky-100'
              }`}
            />
            {errors.phone && (
              <p className="mt-1 text-xs text-rose-600 flex items-center gap-1 font-medium">
                <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                {errors.phone}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="form-email" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
              Email <span className="text-rose-500">*</span>
            </label>
            <input
              type="email"
              id="form-email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="din@virksomhed.dk"
              className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${
                errors.email
                  ? 'border-rose-400 ring-rose-200 focus:border-rose-500'
                  : 'border-slate-200 hover:border-slate-300 focus:border-sky-500 focus:ring-sky-100'
              }`}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-rose-600 flex items-center gap-1 font-medium">
                <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                {errors.email}
              </p>
            )}
          </div>
        </div>

        {/* Transporttype / Service selector */}
        <div>
          <label htmlFor="form-service" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
            Transporttype / Behov
          </label>
          <select
            id="form-service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            className="w-full rounded-xl border border-slate-200 hover:border-slate-300 px-4 py-3 text-sm text-slate-900 bg-white focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all cursor-pointer"
          >
            <option value="Temperaturstyret transport">Temperaturstyret transport (-20°C til +25°C)</option>
            <option value="Ekspreslevering 24/7 (afgang inden for 2 timer)">Ekspreslevering 24/7 (afgang inden for 2 timer)</option>
            <option value="Dag-til-dag godstransport">Dag-til-dag godstransport</option>
            <option value="Palletransport (op til 8 europaller)">Palletransport (op til 8 europaller)</option>
            <option value="Ikke-standard gods / specialtransport">Ikke-standard gods / specialtransport</option>
            <option value="Andet transportbehov">Andet transportbehov</option>
          </select>
        </div>

        {/* Beskrivelse */}
        <div>
          <label htmlFor="form-description" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
            Beskrivelse af opgaven <span className="text-rose-500">*</span>
          </label>
          <textarea
            id="form-description"
            name="description"
            rows="4"
            value={formData.description}
            onChange={handleChange}
            placeholder="Beskriv dit gods, antal paller/kilo, temperaturkrav, afhentnings- og leveringssted samt ønsket tidspunkt..."
            className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${
              errors.description
                ? 'border-rose-400 ring-rose-200 focus:border-rose-500'
                : 'border-slate-200 hover:border-slate-300 focus:border-sky-500 focus:ring-sky-100'
            }`}
          />
          {errors.description && (
            <p className="mt-1 text-xs text-rose-600 flex items-center gap-1 font-medium">
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
              {errors.description}
            </p>
          )}
        </div>

        {/* Server error */}
        {serverError && (
          <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium">
            {serverError}
          </div>
        )}

        {/* Submit button */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-sky-600 hover:bg-sky-500 active:bg-sky-700 disabled:opacity-60 text-white font-bold text-sm sm:text-base transition-colors shadow-md shadow-sky-600/20 cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500/40"
          >
            {submitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin text-sm" aria-hidden="true" />
                <span>Sender forespørgsel...</span>
              </>
            ) : (
              <>
                <span>Send transportforespørgsel</span>
                <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
              </>
            )}
          </button>
        </div>

        <p className="text-[11px] text-slate-600 text-center">
          Vi behandler din forespørgsel hurtigt. Akut behov? Ring direkte på <a href="tel:+4550352035" className="font-bold text-sky-600 hover:underline whitespace-nowrap">50 35 20 35</a> eller skriv til <a href="mailto:transport4u.dk@gmail.com" className="font-bold text-sky-600 hover:underline">transport4u.dk@gmail.com</a>
        </p>
      </div>
    </form>
  )
}
